import React, {memo, useCallback, useEffect, useMemo, useState} from 'react';
import {useFilter} from './context/filterContext';
import {useCatalog} from './context/catalogContext';
import CatalogFilterChoiceListItem from './CatalogFilterChoiceListItem';
import closeIcon from '../../img/sprite/filter-close.svg';
import caretIcon from '../../img/sprite/filter-caret.svg';

const CatalogMobFilterSheet = memo(({onClose}) => {
	const {type, showFilteredTracks} = useCatalog();
	const {
		filters,
		choices,
		draftChoices,
		setDraftChoices,
		radios,
		ranges,
		length,
		draftRanges,
		draftLength,
		applyAllFilters,
		clearFilters,
		setSearches,
	} = useFilter();
	const [openIds, setOpenIds] = useState({});

	const visibleFilters = useMemo(() => {
		if (!Array.isArray(filters)) {
			return [];
		}
		return filters.filter(({id}) => {
			if (type === 'collections' && (id === 'bpm' || id === 'length')) {
				return false;
			}
			return true;
		});
	}, [filters, type]);

  useEffect(() => {
    if (!visibleFilters.length) {
      return;
    }

    setDraftChoices((prev) => {
      const next = {...prev};
      visibleFilters.forEach((filter) => {
        if (next[filter.id] === undefined) {
          next[filter.id] = Array.isArray(choices[filter.id]) ? [...choices[filter.id]] : [];
        }
      });
      return next;
    });
  }, [setDraftChoices, visibleFilters]);

	const getCount = useCallback((filter) => {
		if (filter.type === 'radio') {
			return radios[filter.id] ? 1 : 0;
		}
		const selected = draftChoices[filter.id] ?? choices[filter.id];
		return selected?.length || 0;
	}, [choices, draftChoices, radios]);

	const hasSelection = useMemo(() => {
		const choiceValues = {...choices, ...draftChoices};
		const hasChoices = Object.values(choiceValues).some((value) => value?.length);
		const hasRadio = Object.values(radios).some(Boolean);
		const hasRange = Object.keys(ranges).length > 0 || Object.keys(draftRanges || {}).length > 0;
		const hasLength = Boolean(length.max || draftLength?.max);
		return hasChoices || hasRadio || hasRange || hasLength;
	}, [choices, draftChoices, draftLength, draftRanges, length.max, radios, ranges]);

	const toggleSection = useCallback((id) => {
		setOpenIds((prev) => ({...prev, [id]: !prev[id]}));
	}, []);

	const handleApply = useCallback(() => {
		applyAllFilters();
		showFilteredTracks();
		onClose();
	}, [applyAllFilters, onClose, showFilteredTracks]);

	const handleReset = useCallback(() => {
		clearFilters();
		setSearches([]);
		setDraftChoices({});
		setOpenIds({});
	}, [clearFilters, setDraftChoices, setSearches]);

	return (
		<div className="catalog__mob-sheet">
			<div className="catalog__mob-sheet-header">
				<p className="catalog__mob-sheet-heading">Фильтры</p>
				<button
					type="button"
					className="catalog__mob-sheet-close"
					onClick={onClose}
					aria-label="Закрыть"
				>
					<img src={closeIcon} alt="" width={24} height={24} />
				</button>
			</div>

			<div className="catalog__mob-sheet-list">
				{visibleFilters.map((filter) => {
					const isOpen = Boolean(openIds[filter.id]);
					const count = getCount(filter);

					return (
						<section
							key={filter.id}
							className={`catalog__mob-sheet-section ${isOpen ? 'is-open' : ''}`}
						>
							<button
								type="button"
								className="catalog__mob-sheet-row"
								onClick={() => toggleSection(filter.id)}
							>
								<span className="catalog__mob-sheet-title">{filter.title}</span>
								<span className="catalog__mob-sheet-meta">
									{count > 0 && (
										<span className="catalog__filter-link-count">{count}</span>
									)}
									<img
										className="catalog__mob-sheet-caret"
										src={caretIcon}
										alt=""
										width={24}
										height={24}
									/>
								</span>
							</button>
							<div className="catalog__mob-sheet-body">
								<CatalogFilterChoiceListItem filter={filter} hideActions />
							</div>
						</section>
					);
				})}
			</div>

			<div className={`catalog__mob-sheet-actions ${hasSelection ? '' : 'is-disabled'}`}>
				<button
					type="button"
					className="catalog__filter-button catalog__filter-button--reset"
					onClick={handleReset}
				>
					Сбросить
				</button>
				<button
					type="button"
					className="catalog__filter-button catalog__filter-button--submit"
					onClick={handleApply}
				>
					Применить
				</button>
			</div>
		</div>
	);
});

export default CatalogMobFilterSheet;
